import { cookieStorage } from '../../utils/cookieStorage';
import React, { useState, useEffect, useRef } from 'react';
import pieceTL from '../../assets/images/logo/comarca_piece_tl.png';
import pieceTR from '../../assets/images/logo/comarca_piece_tr.png';
import pieceBL from '../../assets/images/logo/comarca_piece_bl.png';
import pieceBR from '../../assets/images/logo/comarca_piece_br.png';
import emblemImg from '../../assets/images/logo/comarca_emblem_transparent.png';

interface BrandIntroAnimationProps {
    onComplete?: () => void;
    forcePlay?: boolean;
}

export const BrandIntroAnimation: React.FC<BrandIntroAnimationProps> = ({
    onComplete,
    forcePlay = false,
}) => {
    const [shouldRender, setShouldRender] = useState(false);
    // Phases: 'init' | 'assembling' | 'unified' | 'gliding' | 'done'
    const [phase, setPhase] = useState<'init' | 'assembling' | 'unified' | 'gliding' | 'done'>('init');
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setShouldRender(true);

        // Sequence timeline
        const t1 = setTimeout(() => setPhase('assembling'), 120);     // 0.12s: pieces start flying in
        const t2 = setTimeout(() => setPhase('unified'), 1100);       // 1.1s: merge & text reveal
        const t3 = setTimeout(() => setPhase('gliding'), 2100);       // 2.1s: glide to top-left & fade bg
        const t4 = setTimeout(() => {
            setPhase('done');
            if (onComplete) onComplete();
            setTimeout(() => setShouldRender(false), 200);
        }, 3000);                                                     // 3.0s: completed

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
            clearTimeout(t4);
        };
    }, [onComplete]);

    const handleSkip = () => {
        cookieStorage.setItem('comarca_intro_seen', 'true');
        setPhase('done');
        if (onComplete) onComplete();
        setShouldRender(false);
    };

    if (!shouldRender || phase === 'done') {
        return null;
    }

    const isAssembling = phase === 'assembling';
    const isUnified = phase === 'unified';
    const isGliding = phase === 'gliding';

    return (
        <div data-luma-overlay="true"
            ref={containerRef}
            aria-hidden="true"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 99999,
                overflow: 'hidden',
                pointerEvents: isGliding ? 'none' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'opacity 0.75s cubic-bezier(0.4, 0, 0.2, 1)',
                opacity: isGliding ? 0 : 1,
            }}
        >
            {/* ── Background: Co Marca Deep Royal Brand Gradient ── */}
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'radial-gradient(ellipse at 50% 45%, #3c134d 0%, #20082c 45%, #0e0314 100%)',
                    zIndex: 1,
                }}
            />

            {/* ── Ambient Radial Brand Glow ── */}
            <div
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: 'min(90vw, 700px)',
                    height: 'min(90vw, 700px)',
                    background: 'radial-gradient(circle, rgba(193, 52, 139, 0.28) 0%, rgba(97, 37, 123, 0.18) 40%, transparent 70%)',
                    borderRadius: '50%',
                    pointerEvents: 'none',
                    zIndex: 2,
                    filter: 'blur(35px)',
                    transition: 'opacity 0.8s ease, transform 0.8s ease',
                    opacity: isUnified ? 1 : 0.6,
                    transform: `translate(-50%, -50%) scale(${isUnified ? 1.15 : 0.9})`,
                }}
            />

            {/* ── Subtle Aston Martin Style Vignette ── */}
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'radial-gradient(circle at center, transparent 35%, rgba(0, 0, 0, 0.55) 100%)',
                    pointerEvents: 'none',
                    zIndex: 3,
                }}
            />

            {/* ── Master Brand Lockup (Emblem + Text) ── */}
            <div
                style={{
                    position: 'relative',
                    zIndex: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    willChange: 'transform, opacity',
                    transition: isGliding
                        ? 'transform 0.85s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.75s ease'
                        : 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                    transform: isGliding
                        ? 'translate(calc(-50vw + clamp(50px, 6vw, 110px)), calc(-50vh + 32px)) scale(0.36)'
                        : 'translate(0, 0) scale(1)',
                }}
            >
                {/* ── 4 Pieces Container / Emblem ── */}
                <div
                    style={{
                        position: 'relative',
                        width: '120px',
                        height: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '26px',
                    }}
                >
                    {/* Unified Glow Flash behind Emblem */}
                    <div
                        style={{
                            position: 'absolute',
                            inset: '-20px',
                            background: 'radial-gradient(circle, rgba(255, 217, 124, 0.55) 0%, rgba(193, 52, 139, 0.35) 40%, transparent 70%)',
                            borderRadius: '50%',
                            filter: 'blur(12px)',
                            opacity: isUnified ? 1 : 0,
                            transform: isUnified ? 'scale(1.25)' : 'scale(0.5)',
                            transition: 'opacity 0.6s ease, transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                            pointerEvents: 'none',
                        }}
                    />

                    {/* Exact Full Emblem (fades in smoothly when pieces meet) */}
                    <img
                        src={emblemImg}
                        alt="Co Marca Emblem"
                        style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 4px 18px rgba(193, 52, 139, 0.45))',
                            opacity: isUnified || isGliding ? 1 : 0,
                            transform: `scale(${isUnified ? 1.04 : 0.92})`,
                            transition: 'opacity 0.4s ease, transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                            zIndex: 4,
                        }}
                    />

                    {/* ── 4 Pieces Converging into Center ── */}
                    {/* 1. Top Left Piece (Pink / Magenta) */}
                    <img
                        src={pieceTL}
                        alt="TL"
                        style={{
                            position: 'absolute',
                            top: '4px',
                            left: '4px',
                            width: '54px',
                            height: '54px',
                            objectFit: 'contain',
                            willChange: 'transform, opacity',
                            zIndex: 2,
                            opacity: isUnified || isGliding ? 0 : (isAssembling ? 1 : 0),
                            transform: isAssembling
                                ? 'translate(0, 0) rotate(0deg) scale(1)'
                                : 'translate(-75px, -75px) rotate(-35deg) scale(0.6)',
                            transition: 'transform 0.85s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.6s ease',
                            filter: 'drop-shadow(0 0 10px rgba(193, 52, 139, 0.6))',
                        }}
                    />

                    {/* 2. Top Right Piece (Cyan / Blue) */}
                    <img
                        src={pieceTR}
                        alt="TR"
                        style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            width: '54px',
                            height: '54px',
                            objectFit: 'contain',
                            willChange: 'transform, opacity',
                            zIndex: 2,
                            opacity: isUnified || isGliding ? 0 : (isAssembling ? 1 : 0),
                            transform: isAssembling
                                ? 'translate(0, 0) rotate(0deg) scale(1)'
                                : 'translate(75px, -75px) rotate(35deg) scale(0.6)',
                            transition: 'transform 0.85s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.6s ease',
                            filter: 'drop-shadow(0 0 10px rgba(0, 127, 200, 0.6))',
                        }}
                    />

                    {/* 3. Bottom Left Piece (Amber / Orange) */}
                    <img
                        src={pieceBL}
                        alt="BL"
                        style={{
                            position: 'absolute',
                            bottom: '4px',
                            left: '4px',
                            width: '54px',
                            height: '54px',
                            objectFit: 'contain',
                            willChange: 'transform, opacity',
                            zIndex: 2,
                            opacity: isUnified || isGliding ? 0 : (isAssembling ? 1 : 0),
                            transform: isAssembling
                                ? 'translate(0, 0) rotate(0deg) scale(1)'
                                : 'translate(-75px, 75px) rotate(35deg) scale(0.6)',
                            transition: 'transform 0.85s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.6s ease',
                            filter: 'drop-shadow(0 0 10px rgba(234, 132, 6, 0.6))',
                        }}
                    />

                    {/* 4. Bottom Right Piece (Purple) */}
                    <img
                        src={pieceBR}
                        alt="BR"
                        style={{
                            position: 'absolute',
                            bottom: '4px',
                            right: '4px',
                            width: '54px',
                            height: '54px',
                            objectFit: 'contain',
                            willChange: 'transform, opacity',
                            zIndex: 2,
                            opacity: isUnified || isGliding ? 0 : (isAssembling ? 1 : 0),
                            transform: isAssembling
                                ? 'translate(0, 0) rotate(0deg) scale(1)'
                                : 'translate(75px, 75px) rotate(-35deg) scale(0.6)',
                            transition: 'transform 0.85s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.6s ease',
                            filter: 'drop-shadow(0 0 10px rgba(97, 37, 123, 0.6))',
                        }}
                    />
                </div>

                {/* ── Aston Martin Style Letter-Spaced Typography ── */}
                <div
                    style={{
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        willChange: 'letter-spacing, opacity, transform',
                        opacity: isUnified || isGliding ? 1 : 0,
                        transform: `translateY(${isUnified || isGliding ? '0px' : '18px'})`,
                        transition: 'opacity 0.75s cubic-bezier(0.16, 1, 0.3, 1), transform 0.75s cubic-bezier(0.16, 1, 0.3, 1)',
                    }}
                >
                    <h2
                        style={{
                            fontFamily: "'DM Sans', 'Montserrat', sans-serif",
                            fontSize: 'clamp(1.15rem, 2.8vw, 1.75rem)',
                            fontWeight: 700,
                            color: '#ffffff',
                            letterSpacing: isUnified || isGliding ? '0.45em' : '0.15em',
                            textTransform: 'uppercase',
                            margin: 0,
                            paddingLeft: '0.45em', // optical centering for wide letter-spacing
                            transition: 'letter-spacing 0.85s cubic-bezier(0.16, 1, 0.3, 1)',
                            textShadow: '0 2px 20px rgba(255, 255, 255, 0.3), 0 0 35px rgba(193, 52, 139, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.2em',
                        }}
                    >
                        <span>CO MARCA</span>
                        <span style={{ color: '#ffd97c', fontWeight: 800 }}>GROUP</span>
                    </h2>
                    <span
                        style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            letterSpacing: '0.32em',
                            textTransform: 'uppercase',
                            color: 'rgba(255, 255, 255, 0.65)',
                            marginTop: '8px',
                            paddingLeft: '0.32em',
                        }}
                    >
                        INNOVACIÓN & COWORKING
                    </span>
                </div>
            </div>

            {/* ── Discreet Skip Button (Bottom Right) ── */}
            <button
                type="button"
                onClick={handleSkip}
                style={{
                    position: 'absolute',
                    bottom: '24px',
                    right: '28px',
                    zIndex: 10,
                    background: 'rgba(255, 255, 255, 0.08)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.18)',
                    borderRadius: '100px',
                    padding: '8px 18px',
                    color: 'rgba(255, 255, 255, 0.75)',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
                    e.currentTarget.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.75)';
                }}
            >
                Saltar intro →
            </button>
        </div>
    );
};

export default BrandIntroAnimation;


